import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ColumnValuesPayload,
  ColumnValuesResponse,
  GetAdvancedParams,
  GetParams,
  GetResponse,
  Product,
  ProductCreate,
  PostResponse,
} from '@app/shared';
import { convertGetParamsToHttpParams, CompanyService } from '@app/core';

export const PRODUCT_INCLUDE_UNIT_OF_MEASURES = 'UnitOfMeasures';
// Tope de filas al pedir el catálogo entero de productos: el mismo que usa la caché global
// (GlobalValuesService). Quien publique en esa caché debe pedir con este tope para no recortarla.
export const PRODUCT_CATALOG_END_ROW = 5000;

export const PRODUCT_GLOBAL_INCLUDES = [
  PRODUCT_INCLUDE_UNIT_OF_MEASURES,
  'Taxes',
  'SupplierTaxes',
] as const;

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  /** URLs principales relacionadas con Products **/
  ProductsUrl = environment.coreApiBaseUrl + '/api/product';
  ProductsAdvancedUrl = environment.coreApiBaseUrl + '/api/product/get/advanced';

  constructor(
    private http: HttpClient,
    private companyService: CompanyService
  ) {}

  /** Metodos de obtener/crear/actualizar/eliminar Productss **/
  getProducts(
    getParams: GetParams,
    accessToken?: string
  ): Observable<GetResponse> {
    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      'accept': 'application/json',
      'Content-Type': 'application/json',
    });
    
    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.ProductsUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers, // Pasamos los headers
    });
  }

  getProductColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.ProductsUrl}/column-values`,
      payload,
      {headers}
    );
  }

  getAdvancedProducts(getParams: GetAdvancedParams): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<GetResponse>(this.ProductsAdvancedUrl, getParams, {
      headers,
    });
  }

  createProducts(newProduct: ProductCreate): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.ProductsUrl, newProduct, {
      headers,
    });
  }

  updateProducts(Product: Product): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put(this.ProductsUrl, Product, { headers });
  }

  deleteProducts(ProductIds: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.delete(this.ProductsUrl, {
      body: { ids: ProductIds },
      headers,
    });
  }
}
